import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, includes, isEmpty, isNil } from "lodash";
import { Navbar, NavDropdown } from "react-bootstrap";
import { individualSite } from "utils/determineSite";
import { useAuth0 } from "utils/react-auth0-wrapper";
import { intercomHashSelector } from "store/selectors/auth0";
import { setAccountSession } from "actions/accountActions";
import {
  activeAccountState,
  isPreKycSubmissionState,
} from "store/selectors/user";
import {
  IndividualContributionPending,
  IndividualInvestmentProfileUpdate,
  IndividualLoginCreated,
  IndividualPortfolioPending,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  lifecycleStates,
  onboardingStates,
} from "statics/states";
import { FiFileText, FiUser } from "react-icons/fi";

import queryString from "query-string";
// import EmployerReferralLink from "components/EmployerReferralLink";
import "components/navbar/NavBar.scss";
import NavBrand from "components/navbar/NavBrand";
import IndividualNavLeft from "components/navbar/IndividualNavLeft";
import SideBarToggleButton from "components/navbar/SideBarToggleButton";
import IconNavDropdown from "components/navbar/IconNavDropdown";
import NavLoginHeaderText from "components/navbar/NavLoginHeaderText";
import type { RootState } from "types/state";

// Every prop is either redux-injected (mapStateToProps/mapDispatchToProps) or
// `client` (withApollo) — a parent passes nothing — so OwnProps is empty and
// IndividualNavBarProps is just the redux-injected props.
type IndividualNavBarOwnProps = Record<never, never>;

const mapStateToProps = (state: RootState) => {
  // state.user is indexed `unknown`, so get(...) is `unknown`; the runtime
  // value is a lifecycle-state name string — cast at this boundary.
  const userState = get(state.user, "userState.state", "") as string;
  const statesToHideSideBar = [
    ...onboardingStates,
    IndividualLoginCreated,
    IndividualPortfolioPending,
    IndividualContributionPending,
    IndividualInvestmentProfileUpdate,
    IndividualPortfolioUpdate,
    IndividualRequiredUpdatesPending,
  ];

  const userInFinalOnboarding = [
    IndividualPortfolioPending,
    IndividualContributionPending,
  ].includes(userState);
  const userUpdatingPortfolio = [
    IndividualInvestmentProfileUpdate,
    IndividualPortfolioUpdate,
  ].includes(userState);
  const userInRequiredUpdates = [IndividualRequiredUpdatesPending].includes(
    userState
  );

  return {
    shouldHideSideBar: statesToHideSideBar.includes(userState),
    userState: get(state.user, "userState.state", "") as string,
    accountState: activeAccountState(state),
    userId: state.user.id,
    intercomHash: intercomHashSelector(state),
    showSidebar: state.sidebar.showSidebar,
    isPreKycSubmission: isPreKycSubmissionState(state),
    // canReferCompany: canReferCompany(state),
    isIndividualDashboard:
      lifecycleStates.includes(userState) && individualSite(),
    userInFinalOnboarding,
    userUpdatingPortfolio,
    userInRequiredUpdates,
  };
};

const mapDispatchToProps = {
  push,
  setAccountSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type IndividualNavBarProps = PropsFromRedux & IndividualNavBarOwnProps;

const IndividualNavBar = ({
  push,
  intercomHash,
  userState,
  accountState,
  userId,
  client,
  shouldHideSideBar,
  // canReferCompany,
  isPreKycSubmission,
  isIndividualDashboard,
  setAccountSession,
  userInFinalOnboarding,
  userUpdatingPortfolio,
  userInRequiredUpdates,
}: WithApolloClient<IndividualNavBarProps>) => {
  const { pathname } = useLocation();
  const onVerifyEmailRoute = includes(pathname, "verify-email");
  const showChangeEmail = isPreKycSubmission && !onVerifyEmailRoute;
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();

  if (!isNil(userId)) {
    window.analytics.identify(
      userId,
      {
        email: user!.email,
        invitationCode: queryString.parse(window.location.search)
          .invitationCode,
      },
      // @ts-expect-error Segment's identify accepts a 3rd `options` arg
      // (integrations) at runtime; the global type in types/globals.d.ts only
      // declares 2. Preserving the original runtime call.
      {
        integrations: {
          Intercom: {
            user_hash: intercomHash,
          },
        },
      }
    );
    Sentry.setUser({ id: userId });
  }

  const logoutWithRedirect = async () => {
    // if accountState exists then we are on an active account and we need to clear the session
    if (!isEmpty(accountState)) {
      // client is optional on WithApolloClient but is always injected by
      // withApollo at runtime; cast at this action-creator call site.
      await setAccountSession(client as ApolloClient<object>, "");
    }

    window.analytics.track("Logged Out");
    // Segment's reset() is a real runtime method, but the global
    // Window["analytics"] type in types/globals.d.ts does not declare it —
    // cast at this boundary until that global type gains reset().
    (
      window.analytics as typeof window.analytics & { reset: () => void }
    ).reset();

    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  const individualLifecycleStates =
    lifecycleStates.includes(userState) ||
    // accountState is `string | null` (activeAccountState); includes() expects
    // a string. A null value simply won't match — cast at this call site.
    lifecycleStates.includes(accountState as string);

  return (
    <div>
      <header id="header" className={"icon-header"}>
        <Navbar>
          <div className="nav-left">
            <NavBrand />
            <Navbar.Text>
              {isIndividualDashboard ? (
                <IndividualNavLeft />
              ) : (
                <p className="portal">Individual</p>
              )}
            </Navbar.Text>
          </div>

          <div className="nav-right">
            {!isAuthenticated && (
              // NavLoginHeaderText calls onClick with a v1-style { redirect_uri },
              // which auth0-spa-js v2 ignores (redirect_uri moved under
              // authorizationParams) — the arg is inert. Pre-existing behavior,
              // preserved via this cast rather than reshaped.
              <NavLoginHeaderText
                onClick={
                  loginWithRedirect as unknown as (args: {
                    redirect_uri: string;
                  }) => void
                }
              />
            )}

            {isAuthenticated && (
              <>
                <IconNavDropdown
                  userName={user!.nickname}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- preserved: logoutWithRedirect is async (returns a Promise) but onLogout expects () => void; the fire-and-forget call matches the original runtime behavior
                  onLogout={logoutWithRedirect}
                  shouldHideSideBar={shouldHideSideBar}
                  showNavAvatar={false}
                >
                  {showChangeEmail && (
                    <NavDropdown.Item
                      eventKey="4.1"
                      onClick={() => push("/individual/change-email")}
                    >
                      <FiUser
                        className="dropdown-icons"
                        size={14}
                        color={"#32325d"}
                        fill={"#FFFFFF"}
                        stroke={"#32325d"}
                      />
                      Change Email
                    </NavDropdown.Item>
                  )}

                  {individualLifecycleStates && (
                    <>
                      {!userInRequiredUpdates && (
                        <NavDropdown.Item
                          eventKey="4.1"
                          disabled={
                            userInFinalOnboarding || userUpdatingPortfolio
                          }
                          onClick={() => push("/dashboard/profile/info")}
                        >
                          <FiUser
                            className="dropdown-icons"
                            size={14}
                            color={"#32325d"}
                            fill={"#FFFFFF"}
                            stroke={"#32325d"}
                          />
                          Profile
                        </NavDropdown.Item>
                      )}
                      <NavDropdown.Item
                        eventKey="4.2"
                        onClick={() => push("/dashboard/documents")}
                      >
                        <FiFileText
                          color={"#FFFFFF"}
                          fill={"#FFFFFF"}
                          stroke={"#32325d"}
                          className="dropdown-icons"
                          size={14}
                        />
                        Documents
                      </NavDropdown.Item>
                    </>
                  )}
                </IconNavDropdown>

                {!shouldHideSideBar && <SideBarToggleButton />}
              </>
            )}
          </div>
        </Navbar>
      </header>
    </div>
  );
};

export default connector(withApollo<IndividualNavBarProps>(IndividualNavBar));
